import { useState, useEffect, useCallback } from 'react';
import { useFragment, graphql } from 'react-relay';
import { BigNumber } from 'ethers';
import type { GenericERC721Token, GenericERC1155Token } from '1stdibs-contracts/typechain';
import {
    getGenericTokenContract,
    getGeneric1155TokenContract,
} from 'dibs-crypto-utils/exports/getContract';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { usePrevious } from 'dibs-react-hooks/exports/usePrevious';

import { useBurnNft_nftItem$key } from './__generated__/useBurnNft_nftItem.graphql';

export type BurnNftStatus = null | 'success' | 'loading';

export const useBurnNft = (
    nftItemRef: useBurnNft_nftItem$key | null = null
): [
    handleBurnNft: (editions: number) => Promise<void>,
    burnNftStatus: BurnNftStatus,
    burnedEditions: number
] => {
    const { library, account } = useWeb3Connection();
    const [burnNftStatus, setBurnNftStatus] = useState<BurnNftStatus>(null);
    const [burnedEditions, setBurnedEditions] = useState<number>(1);

    const nftItem = useFragment(
        graphql`
            fragment useBurnNft_nftItem on NftItem {
                tokenId
                wasBurned
                contract {
                    address
                }
                balanceForUser(userId: $userId) @include(if: $fetchUser) {
                    balance
                }
                totalSupply
                isERC1155
            }
        `,
        nftItemRef
    );

    const totalSupply = nftItem?.totalSupply;
    const startingSupply = usePrevious(totalSupply);

    useEffect(() => {
        if (nftItem?.wasBurned) {
            setBurnNftStatus('success');
        }
    }, [nftItem]);

    useEffect(() => {
        if (
            totalSupply &&
            startingSupply &&
            startingSupply !== totalSupply &&
            burnNftStatus === 'loading'
        ) {
            setBurnNftStatus('success');
        }
    }, [totalSupply, startingSupply, burnNftStatus]);

    const handleBurnNft = useCallback(
        async (editions: number): Promise<void> => {
            const signer = library?.getSigner(account ?? '');
            if (editions) {
                setBurnedEditions(editions);
            }
            if (signer && library && account && nftItem?.contract?.address) {
                const token = nftItem?.isERC1155
                    ? await getGeneric1155TokenContract(library, nftItem.contract.address)
                    : await getGenericTokenContract(library, nftItem.contract.address);
                try {
                    const tx = nftItem?.isERC1155
                        ? await (token as GenericERC1155Token)
                              ?.connect(signer)
                              ['burn(address,uint256[],uint256[])'](
                                  account,
                                  [BigNumber.from(nftItem.tokenId)],
                                  [BigNumber.from(editions)]
                              )
                        : await (token as GenericERC721Token)
                              ?.connect(signer)
                              .burn(BigNumber.from(nftItem.tokenId));
                    setBurnNftStatus('loading');
                    await tx?.wait();
                } catch (e: $TSFixMe) {
                    // fail silently
                    setBurnNftStatus(null);
                }
            }
        },
        [account, library, nftItem]
    );
    return [handleBurnNft, burnNftStatus, burnedEditions];
};
